import * as React from "react";
import { Container, Grid, Header } from "semantic-ui-react";

import { AccordionQuestionnaireContainer } from "../../questionnaires/components/AccordionQuestionnaireContainer";
import { AccordionQuestionnaireSection } from "../../questionnaires/components/AccordionQuestionnaireSection";
import { DropdownQuestion } from "../../questionnaires/components/questions/DropdownQuestion";
import { QuestionConfig } from "../../questionnaires/questionnaireConfig";
import { masterConfig } from "../masterConfig";
import * as questionIds from "../questionIds";
import { agreementLevelOptions, applicabilityAndAgreementLevelOptions, EmployeeSatisfactionSurveyModel, EMPLOYEE_SATISFACTION_SURVEY_KEY } from "../models";
import { TextQuestion } from "../../questionnaires/components/questions/TextQuestion";
import { RadioButton, RadioButtonQuestion, YesOrNo } from "../../questionnaires/components/questions/RadioButtonQuestion";
import { QuestionnaireTypes } from "../../questionnaires/model";

interface OwnProps {
    isReadonly: boolean;
    loadQuestionnaire?: () => Promise<void>;
    submitQuestionnaire?: (questionnaire: EmployeeSatisfactionSurveyModel) => Promise<boolean>;
    questionnaire: EmployeeSatisfactionSurveyModel;
}

export const EmployeeSatisfactionSurveyForm: React.FunctionComponent<OwnProps> = (props: OwnProps) => {

    const isReadonly = props.isReadonly;

    const loadQuestionnaire = () => {
        if (props.loadQuestionnaire) {
            props.loadQuestionnaire();
        }
    };

    const getQuestionConfig = (questionId: string): QuestionConfig => {
        return masterConfig.find(c => c.id === questionId) || { id: questionId, disabled: true };
    };

    return (
        <Container className="referral-report">
            <Grid container>
                <Grid.Row className="report-title-row">
                    <Grid.Column>
                        <Header as="h1">Satisfaction Survey</Header>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Grid container>
                <Grid.Row>
                    <Grid.Column>
                        <p>
                            Your opinion of our Occupational Health and Well Being service is important to us.
                            It would really help if you could take a few moments to share your experience with our service,
                            so that we can pass on any positive comments to our team or if necessary, use your feedback to improve our service.
                        </p>
                        <p>
                            You can find out more information about how the data is held securely by accessing our privacy statement
                            <a href="https://tphealth.co.uk/privacy-statement" target="_blank" rel="noopener noreferrer"> <strong>here</strong></a>.
                        </p>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Container>
                <Grid>
                    <AccordionQuestionnaireContainer
                        loadQuestionnaire={loadQuestionnaire}
                        submitQuestionnaire={() => props.submitQuestionnaire(props.questionnaire)}
                        readonly={isReadonly}
                        questionnaireKey={EMPLOYEE_SATISFACTION_SURVEY_KEY}
                        questionnaireType={QuestionnaireTypes.Other}
                    >
                        <AccordionQuestionnaireSection
                            title="Satisfaction Survey"
                            id="SatisfactionSurvey"
                            hideNextButton={true}
                            active={true}
                        >
                            <DropdownQuestion
                                questionConfig={getQuestionConfig(questionIds.OverallSatisfactionWithExperienceWithTPHealth)}
                                readonly={isReadonly}
                                mandatory={true}
                                options={agreementLevelOptions.sort(o => o.order)}
                                hideDivider
                            />
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.OverallSatisfactionWithExperienceWithTPHealthJustification)}
                                readonly={isReadonly}
                                mandatory={false}
                                multiline
                            />
                            <DropdownQuestion
                                questionConfig={getQuestionConfig(questionIds.TPHealthMadeItEasyForMeToMakeAnAppointment)}
                                readonly={isReadonly}
                                mandatory={true}
                                options={agreementLevelOptions.sort(o => o.order)}
                                hideDivider
                            />
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.TPHealthMadeItEasyForMeToMakeAnAppointmentJustification)}
                                readonly={isReadonly}
                                mandatory={false}
                                multiline
                            />
                            <DropdownQuestion
                                questionConfig={getQuestionConfig(questionIds.HappinessLevelWithTheWayTheOccupationalHealthConsultationWasCarriedOut)}
                                readonly={isReadonly}
                                mandatory={true}
                                options={agreementLevelOptions.sort(o => o.order)}
                                hideDivider
                            />
                            <TextQuestion
                                // eslint-disable-next-line max-len
                                questionConfig={getQuestionConfig(questionIds.HappinessLevelWithTheWayTheOccupationalHealthConsultationWasCarriedOutJustification)}
                                readonly={isReadonly}
                                mandatory={false}
                                multiline
                            />
                            <DropdownQuestion
                                questionConfig={getQuestionConfig(questionIds.ReportToMyManagerWasBalancedAndImpartial)}
                                readonly={isReadonly}
                                mandatory={true}
                                options={applicabilityAndAgreementLevelOptions.sort(o => o.order)}
                                hideDivider
                            />
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.ReportToMyManagerWasBalancedAndImpartialJustification)}
                                readonly={isReadonly}
                                mandatory={false}
                                multiline
                            />
                            <RadioButtonQuestion
                                questionConfig={getQuestionConfig(questionIds.HowLikelyWouldYouBeToRecommendOccupationalHealthService)}
                                readonly={isReadonly}
                                mandatory
                                horizontal
                            >
                                <RadioButton value="10" label="10" />
                                <RadioButton value="9" label="9" />
                                <RadioButton value="8" label="8" />
                                <RadioButton value="7" label="7" />
                                <RadioButton value="6" label="6" />
                                <RadioButton value="5" label="5" />
                                <RadioButton value="4" label="4" />
                                <RadioButton value="3" label="3" />
                                <RadioButton value="2" label="2" />
                                <RadioButton value="1" label="1" />
                                <RadioButton value="0" label="0" />
                            </RadioButtonQuestion>
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.IfAwardedLessThanWhatCanWeDoToEarnBetterScore)}
                                readonly={isReadonly}
                                mandatory={false}
                                multiline
                            />
                            <RadioButtonQuestion
                                questionConfig={getQuestionConfig(questionIds.DoYouFeelThatYourConsultationWithOccupationalHealthHasHelped)}
                                readonly={isReadonly}
                                mandatory
                                horizontal
                            >
                                <RadioButton label="Yes" value={YesOrNo.Yes} />
                                <RadioButton label="No" value={YesOrNo.No} />
                            </RadioButtonQuestion>
                            <RadioButtonQuestion
                                questionConfig={getQuestionConfig(questionIds.DoYouFeelThatTheReportAndConsultationWillHelpYouToReturnToWork)}
                                readonly={isReadonly}
                                mandatory
                                horizontal
                            >
                                <RadioButton label="Yes" value={YesOrNo.Yes} />
                                <RadioButton label="No" value={YesOrNo.No} />
                                <RadioButton label="Not Applicable" value={YesOrNo.NotApplicable} />
                            </RadioButtonQuestion>
                        </AccordionQuestionnaireSection>
                    </AccordionQuestionnaireContainer>
                </Grid>
            </Container>
        </Container>
    );
};
